import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { JobPostingApplicantIndex } from '@app/modules/applicant-tracker/meta/view-contexts';
import {
    ApplicantIndividualMessageService,
    ApplicantService,
    SettingsIntegrationService,
} from '@app/modules/applicant-tracker/services';
import { AnalyticService, NotifyService } from '@app/services';
import { ApplicantFilters } from '@applicant-tracker/enums/applicant-filters.enum';
import { JobPostingApplicantsIndexTable } from '@applicant-tracker/services/tables/applicants-table/job-posting-applicants-index.table';
import { RoutingStateService } from '@common/services/routing-state.service';
import { Applicant } from '@models/recruiting/applicant.model';
import { JobFunnelMessageTemplate } from '@models/recruiting/job-funnel-message-template.model';
import { JobFunnel } from '@models/recruiting/job-funnel.model';
import { JobPosting } from '@models/recruiting/job-posting.model';
import { Column } from '@table/classes/column.class';
import { FilterValueChange } from '@table/interfaces/filter-value-change.interface';

@Component({
    templateUrl: './job-posting-applicant-index.template.html',
    styleUrls: ['./job-posting-applicant-index.style.scss'],
})
export class JobPostingApplicantIndexView implements OnInit {
    jobPosting: JobPosting;
    isLoading = false;

    applicant: Applicant;
    applicantJobFunnelMessageTemplate: JobFunnelMessageTemplate;

    isChangeFunnelStageModalOpen = false;
    searchPlaceHolder = 'search';
    searchWidth = 250;

    constructor(
        public table: JobPostingApplicantsIndexTable,
        private route: ActivatedRoute,
        private router: Router,
        public applicantIndividualMessageService: ApplicantIndividualMessageService,
        private applicantService: ApplicantService,
        private integrationService: SettingsIntegrationService,
        private notify: NotifyService,
        private routingState: RoutingStateService,
        private analyticService: AnalyticService
    ) {
        this.jobPosting = this.route.snapshot.data.models.get('jobPosting');
        this.applicantService.updateContext(JobPostingApplicantIndex);
        this.applicantService.indexPaginator = this.table.paginator;
    }

    async ngOnInit(): Promise<void> {
        this.searchPlaceHolder = 'applicant-tracker.applicants.searchByNameOrKeywordsInDocuments';
        this.searchWidth = 320;
        this.isLoading = true;
        this.table.scopeToJobPosting(this.jobPosting);
        this.table.reload();
        this.isLoading = false;

        // TODO: Remove this once the routing state service working correctly.
        this.routingState.history;
    }

    onAllFiltersCleared(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingApplicantsClearFilters);
    }

    onFilterChange(change: FilterValueChange): void {
        switch (change.field) {
            case ApplicantFilters.jobPosting:
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingApplicantsFilterAppliedJobPosting);
                break;
            case ApplicantFilters.status:
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingApplicantsFilterAppliedStatus);
                break;
            case ApplicantFilters.jobFunnel:
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingApplicantsFilterAppliedJobFunnel);
                break;
            case ApplicantFilters.rating:
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingApplicantsFilterAppliedRating);
                break;
            default:
                throw new Error('applicant-tracker.applicants.analyticEventIsMissingForApplicantFilter' + change.field);
        }
    }

    onSearchQueryChange(_query: string): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingApplicantsSearch);
    }

    async onChangeFunnelStage(dropdownSelection: { model: Applicant; column: Column }): Promise<void> {
        this.applicant = dropdownSelection.model;

        const jobFunnel: JobFunnel = await JobFunnel.with(['jobFunnelMessageTemplate']).find(
            this.applicant.jobFunnelId
        );

        if (!jobFunnel.jobFunnelMessageTemplate) {
            this.notify.success('applicant-tracker.applicants.applicantStatusChanged');
            return;
        }

        this.isChangeFunnelStageModalOpen = true;

        this.applicantJobFunnelMessageTemplate = jobFunnel.jobFunnelMessageTemplate;
    }

    onCloseChangeFunnelStage(): void {
        this.isChangeFunnelStageModalOpen = false;

        this.notify.success('applicant-tracker.applicants.applicantStatusChanged');

        this.analyticService.trackEvent(
            AnalyticEvents.RecruitingJobPostingFunnelChangeApplicantFunnelStageClickSkipEmail
        );
    }

    async onConfirmChangeFunnelStage(): Promise<void> {
        try {
            await this.applicantIndividualMessageService.sendApplicantIndividualMessage(
                this.applicant.id,
                this.applicantJobFunnelMessageTemplate
            );

            this.notify.success({
                key: 'applicant-tracker.applicants.subjectEmailSentToApplicant',
                params: { subject: this.applicantJobFunnelMessageTemplate.subject },
            });

            this.notify.success('applicant-tracker.applicants.applicantStatusChanged');

            this.analyticService.trackEvent(
                AnalyticEvents.RecruitingJobPostingFunnelChangeApplicantFunnelStageClickSendEmail
            );
        } catch (err) {
            this.notify.error({
                key: 'applicant-tracker.applicants.emailFailedToSendToApplicant',
                params: { subject: this.applicantJobFunnelMessageTemplate.subject },
            });
            this.notify.error('applicant-tracker.applicants.applicantStatusChangeFailed');
        }

        this.isChangeFunnelStageModalOpen = false;
    }

    onCreate(): void {
        this.router.navigate(['/', 'recruiting', 'applicants', 'create'], {
            queryParams: { jobPostingId: this.jobPosting.id },
        });
    }

    async onPause(pause: boolean): Promise<void> {
        try {
            this.isLoading = true;
            this.jobPosting = await this.integrationService.pauseJobIntegration(this.jobPosting.id, pause);
            this.notify.success({
                key: 'applicant-tracker.applicants.jobPostingHasBeenActionSuccessfully',
                params: { action: pause ? 'paused' : 'unpaused' },
            });
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    async onExport(format: 'csv' | 'xlsx') {
        const filters = {};

        for (const [key, value] of this.table.activeFilters.entries()) {
            filters[key] = value;
        }

        filters['atsJobId'] = [this.jobPosting.id];
        filters['withJobPostingQuestions'] = true;

        await this.applicantService.exportApplicants(
            format,
            this.table.paginator.search,
            filters,
            this.table.paginator.sort
        );

        switch (format) {
            case 'csv':
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingApplicantsExportCSV);
                break;
            case 'xlsx':
                this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingApplicantsExportXLSX);
                break;
        }
    }
}
